import { useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'
import { qk } from '@/api/queryKeys'
import { SSE_BACKOFF_INITIAL_MS, SSE_BACKOFF_MAX_MS } from '@/config/constants'

/**
 * Single global Server-Sent Events channel.
 *
 * The hook MUST be mounted exactly once at the root of the React tree
 * (`__root.tsx`). Per-feature components do NOT instantiate their own
 * `EventSource` — browsers cap at 6 connections per origin and the legacy
 * frontend regularly exhausted that ceiling, freezing the tab
 * (PAIN_POINTS #9, architecture invariant C-07).
 *
 * Endpoint: `GET /api/events`. Go writes named SSE messages
 * (`event: <name>\ndata: <json>\n\n`); EventSource delivers those ONLY
 * to listeners registered via `addEventListener(name, …)`. The default
 * `onmessage` handler does NOT fire for named events — that subtle bit
 * of the spec was the reason the previous implementation never invalidated
 * any cache.
 */

type DispatchFn = (data: unknown, qc: QueryClient, wsId: number) => void

const HANDLERS: Record<string, DispatchFn> = {
  /** Task finished one stage (EL / FAL). Invalidates everything that lists
   *  rows so the user sees the new status without waiting on polling. */
  row_status_change: (_data, qc, wsId) => {
    qc.invalidateQueries({ queryKey: ['ws', wsId, 'tasks', 'v2'] })
    qc.invalidateQueries({ queryKey: ['ws', wsId, 'projects', 'rows'] })
    // Use prefix-only keys (`tasksAll`) — the filtered queries on the
    // tasks pages append a filter object as the last key element, and
    // `tasks()` without args appends a literal `undefined` that would
    // not partial-match those filtered keys.
    qc.invalidateQueries({ queryKey: ['ws', wsId, 'quick', 'tasks'] })
    qc.invalidateQueries({ queryKey: qk.ws(wsId).data.tasksAll() })
  },
  /** Streaming progress (FAL queue ticker). Patches the row cache
   *  in-place with the new pct/stage so the table animates without
   *  triggering a full refetch — this fires every 2s per active row
   *  during FAL polling and an invalidate-each-time would melt the
   *  network tab.
   *
   *  Payload shape (from `cmd/server/main.go:falProc.SetProgress`):
   *    { row_id, project_id, task_type, progress, stage }
   *
   *  We only patch when the row + project ids are known and the row
   *  list is already in the cache (don't materialise empty entries).
   */
  task_progress: (data, qc, wsId) => {
    const d = data as
      | {
          row_id?: number
          project_id?: number
          progress?: number
          stage?: string
        }
      | undefined
    if (!d || typeof d.row_id !== 'number') return
    if (typeof d.project_id !== 'number') return
    const key = qk.ws(wsId).projects.rows(d.project_id)
    qc.setQueriesData<unknown>({ queryKey: key }, (prev: unknown) => {
      if (!Array.isArray(prev)) return prev
      const list = prev as Array<Record<string, unknown>>
      const idx = list.findIndex((r) => r['id'] === d.row_id)
      if (idx === -1) return prev
      const next = list.slice()
      next[idx] = {
        ...list[idx],
        live_progress_pct: d.progress ?? null,
        live_progress_stage: d.stage ?? null,
      }
      return next
    })
  },
  project_stats: (_data, qc, wsId) => {
    qc.invalidateQueries({ queryKey: ['ws', wsId, 'projects'] })
  },
  notification: (_data, qc, wsId) => {
    qc.invalidateQueries({ queryKey: qk.ws(wsId).notifications.list() })
    qc.invalidateQueries({ queryKey: qk.ws(wsId).notifications.unread() })
  },
  /** Legacy / generic task list bump. Kept for backwards-compat. */
  task_update: (data, qc, wsId) => {
    qc.invalidateQueries({ queryKey: ['ws', wsId, 'tasks', 'v2'] })
    const d = data as { project_id?: number } | undefined
    if (typeof d?.project_id === 'number') {
      qc.invalidateQueries({
        queryKey: qk.ws(wsId).projects.rowsAll(d.project_id),
      })
    }
  },
  project_update: (_data, qc, wsId) => {
    qc.invalidateQueries({ queryKey: ['ws', wsId, 'projects'] })
  },
}

export function useSSE(): void {
  const qc = useQueryClient()
  const isAuth = useAuthStore((s) => s.isAuthenticated)
  const wsId = useWorkspaceStore((s) => s.activeWsId)
  const seenUnknownEvents = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!isAuth || wsId === null) return

    let es: EventSource | null = null
    let reconnectTimer: number | null = null
    let backoff = SSE_BACKOFF_INITIAL_MS
    let cancelled = false

    const connect = (): void => {
      if (cancelled) return
      // EventSource cannot send custom headers, so the workspace id has to
      // ride on the query string. Backend's WorkspaceContext middleware
      // reads `?ws=` as a fallback when X-Workspace-ID is absent.
      const url = `/api/events?ws=${encodeURIComponent(String(wsId))}`
      const source = new EventSource(url, { withCredentials: true })
      es = source

      source.onopen = (): void => {
        backoff = SSE_BACKOFF_INITIAL_MS
      }

      // Wire one listener per known event name — addEventListener is
      // required because Go uses named SSE messages and `onmessage` never
      // fires for those.
      for (const [name, handler] of Object.entries(HANDLERS)) {
        source.addEventListener(name, (ev) => {
          try {
            const data = JSON.parse((ev as MessageEvent<string>).data)
            handler(data, qc, wsId)
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(`[useSSE] failed to parse ${name}`, e)
          }
        })
      }

      // Default (unnamed) channel — used by `:keepalive` heartbeats and
      // any bare `data:` lines. We only log unknown payloads here to
      // surface protocol drift.
      source.onmessage = (ev: MessageEvent<string>): void => {
        if (!ev.data || ev.data.trim() === '') return
        if (!seenUnknownEvents.current.has(ev.data)) {
          seenUnknownEvents.current.add(ev.data)
          // eslint-disable-next-line no-console
          console.warn('[useSSE] unnamed SSE message', ev.data)
        }
      }

      source.onerror = (): void => {
        source.close()
        es = null
        if (cancelled) return
        const delay = Math.min(backoff, SSE_BACKOFF_MAX_MS)
        backoff = Math.min(backoff * 2, SSE_BACKOFF_MAX_MS)
        reconnectTimer = window.setTimeout(connect, delay)
      }
    }

    connect()

    const onUnload = (): void => {
      es?.close()
    }
    window.addEventListener('beforeunload', onUnload)

    return () => {
      cancelled = true
      window.removeEventListener('beforeunload', onUnload)
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      es?.close()
      es = null
    }
  }, [isAuth, wsId, qc])
}

/**
 * Test helper — drives a single SSE handler without spinning up a real
 * EventSource. Returns true if the named handler exists.
 */
export function dispatchTest(
  eventName: string,
  data: unknown,
  qc: QueryClient,
  wsId: number,
): boolean {
  const handler = HANDLERS[eventName]
  if (!handler) return false
  handler(data, qc, wsId)
  return true
}
